import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

// Director payroll view: actual clocked hours per staff for one Mon–Sun
// week, paired from clock_in/clock_out events. This is a cross-check
// against the schedule-based Payroll page (which shows *planned* hours).

function mondayOf(date) {
  const d = new Date(date);
  const dow = d.getDay();
  const back = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - back);
  d.setHours(0, 0, 0, 0);
  return d;
}
function toISO(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtHm(h, m) {
  if (!h && !m) return '0h';
  return `${h}h${m ? ` ${String(m).padStart(2, '0')}m` : ''}`;
}
const rangeLabel = (weekStart) => {
  const end = addDays(weekStart, 6); // Mon → Sun
  const opt = { month: 'short', day: 'numeric' };
  return `${weekStart.toLocaleDateString('en-US', opt)} – ${end.toLocaleDateString('en-US', opt)}`;
};

export default function ClockedHoursPage() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/attendance/hours?start=${toISO(weekStart)}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load clocked hours'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weekStart]);

  const isThisWeek = useMemo(
    () => toISO(weekStart) === toISO(mondayOf(new Date())),
    [weekStart]
  );
  const staff = data?.staff || [];

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClockIcon className="w-6 h-6" /> Clocked Hours
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Actual time staff clocked, by week (Mon–Sun). For payroll — cross-check against the{' '}
            <Link to="/payroll" className="text-orange-600 hover:underline">schedule-based Payroll</Link> page.
          </p>
        </div>

        {/* Week navigator */}
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 py-2 mb-4">
          <button onClick={() => setWeekStart((w) => mondayOf(addDays(w, -7)))}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600" aria-label="Previous week">
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="text-sm font-semibold text-gray-900">{rangeLabel(weekStart)}</div>
            <button onClick={() => setWeekStart(mondayOf(new Date()))} disabled={isThisWeek}
              className={`text-xs ${isThisWeek ? 'text-gray-400' : 'text-orange-600 hover:underline'}`}>
              {isThisWeek ? 'This week' : 'Jump to this week'}
            </button>
          </div>
          <button onClick={() => setWeekStart((w) => mondayOf(addDays(w, 7)))}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600" aria-label="Next week">
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : staff.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
            <ClockIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">No clocked time this week yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600">
                  <th className="text-left px-4 py-3 font-semibold">Staff</th>
                  <th className="text-center px-3 py-3 font-semibold hidden sm:table-cell">Shifts</th>
                  <th className="text-right px-4 py-3 font-semibold">Hours</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((s) => (
                  <tr key={s.staff_id} className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{s.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-400 capitalize">{s.role}</span>
                        {s.has_open && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> clocked in
                          </span>
                        )}
                        {s.has_missing && (
                          <span className="text-[11px] text-amber-600" title="A shift is missing a clock-out — correct it on the staff member's Time & Events tab">
                            ⚠ missing punch
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center px-3 py-3 text-gray-500 hidden sm:table-cell tabular-nums">{s.sessions}</td>
                    <td className="text-right px-4 py-3 font-semibold text-gray-900 tabular-nums">{fmtHm(s.h, s.m)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td className="px-4 py-3 font-semibold text-gray-700">Total</td>
                  <td className="hidden sm:table-cell" />
                  <td className="text-right px-4 py-3 font-bold text-gray-900 tabular-nums">
                    {data ? fmtHm(data.h, data.m) : '0h'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <p className="text-[11px] text-gray-400 mt-3">
          Missing punches count as 0 hours until corrected. A shift open longer than 16 hours is treated as a
          missed clock-out.
        </p>
      </div>
    </div>
  );
}
